import KeyboardKeyIcon from '@/components/ui/keyboard-key-icon';
import HotKeyListener from '@/components/ux/hot-key-listener';
import { toast } from '@/hooks/use-toast';
import { HOTKEYS } from '@/lib/hotkeys';
import { pdkAxios } from '@/lib/pdk-axios';
import { cn } from '@/lib/utils';
import { vehicleFromIndexerStore } from '@/models/stores/vehicle-listings';
import type { VehicleChargingPlug, VehicleFromIndexer } from '@/models/types';
import { useMutation } from '@tanstack/react-query';
import { Zap } from 'lucide-react';
import { useMemo, useRef } from 'react';
import {
  ComboboxPopover,
  type Option,
} from '../../ui/combobox/combobox-popover';
import { VehicleChargingPlugBadge } from './charger-type-badge';

export const getChargingPlugOptions = ({
  className,
}: {
  className: string;
}): Option<VehicleChargingPlug>[] => {
  return [
    {
      value: 'None',
      label: 'None',
      icon: <Zap className={className} />,
      hotkey: '0',
    },
    {
      value: 'Tesla NACS',
      label: 'Tesla NACS',
      icon: <Zap className={className} />,
      hotkey: '1',
    },
    {
      value: 'CHAdeMO and J1772',
      label: 'CHAdeMO and J1772',
      icon: <Zap className={className} />,
      hotkey: '2',
    },
    {
      value: 'J1772 and CCS1',
      label: 'J1772 and CCS1',
      icon: <Zap className={className} />,
      hotkey: '3',
    },
    {
      value: 'J1772',
      label: 'J1772',
      icon: <Zap className={className} />,
      hotkey: '4',
    },
  ];
};

export const CHARGING_PLUG_OPTIONS_MAP = getChargingPlugOptions({
  className: 'h-3 w-3',
});

export type VehicleChargingPlugSelectorProps = {
  id: string;
  value: VehicleFromIndexer['charging_plug'] | null;
  hideCopy?: boolean;
  side?: 'left' | 'right' | 'bottom' | 'top';
  withHotkeyListener?: boolean;
  buttonVariant?: 'outline' | 'ghost';
  className?: string;
};

export function VehicleChargingPlugSelector({
  id,
  value,
  hideCopy = false,
  side = 'left',
  withHotkeyListener = false,
  buttonVariant = 'ghost',
  className,
}: VehicleChargingPlugSelectorProps) {
  const chargingPlugButtonRef = useRef<HTMLButtonElement>(null);

  const updateChargingPlugMutation = useMutation({
    onMutate: async ({ charging_plug }) => {
      vehicleFromIndexerStore.updateChargingPlug({ id, charging_plug });
    },
    mutationFn: async ({
      charging_plug,
    }: {
      charging_plug: VehicleFromIndexer['charging_plug'];
    }) => {
      return await pdkAxios.post(`/inventory/vehicles/${id}/charging_plug`, {
        charging_plug,
      });
    },
    onError: (error) => {
      toast({
        title: 'Error updating charging plug',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const handleChargingPlugChange = (
    newChargingPlug: VehicleFromIndexer['charging_plug'],
  ) => {
    updateChargingPlugMutation.mutate({ charging_plug: newChargingPlug });
  };

  const currentOption = useMemo(() => {
    return CHARGING_PLUG_OPTIONS_MAP.find((option) => option.value === value);
  }, [value]);

  return (
    <div>
      <ComboboxPopover
        endAdornment={
          <KeyboardKeyIcon>{HOTKEYS.VEHICLE_CHARGING_PLUG}</KeyboardKeyIcon>
        }
        placeholder="Set charging plug..."
        currentOption={currentOption}
        onChange={handleChargingPlugChange}
        emptyMessage="No charging plug found"
        options={CHARGING_PLUG_OPTIONS_MAP}
        side={side}
        align="start"
        onKeyDown={(e) => {
          const keyPressed = e.key;

          const option = CHARGING_PLUG_OPTIONS_MAP.find(
            (option) => option.hotkey === keyPressed,
          );

          if (option) {
            handleChargingPlugChange(option.value);
            e.preventDefault();
            e.stopPropagation();
          }
        }}
      >
        <div>
          <VehicleChargingPlugBadge
            value={value ?? 'None'}
            hideCopy={hideCopy}
            buttonRef={chargingPlugButtonRef}
            buttonVariant={buttonVariant}
            className={cn('pl-0', className)}
          />
        </div>
      </ComboboxPopover>
      {withHotkeyListener && (
        <HotKeyListener
          hotkeys={HOTKEYS.VEHICLE_CHARGING_PLUG}
          onHotkeyPressed={() => {
            chargingPlugButtonRef.current?.click();
          }}
        />
      )}
    </div>
  );
}
